import { formatInTimeZone } from 'date-fns-tz';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { OUTREACH_ANALYTICS_TIMEZONE } from '@apps/web/components/admin/outreach_observability/constants';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { type SwarmWorkerFleetSample } from '@libraries/typescript/api_client';

type SwarmWorkerHistoryChartProps = {
    readonly history: Array<SwarmWorkerFleetSample>;
};

type SwarmWorkerHistoryPoint = {
    readonly label: string;
    readonly processingDecisions: number;
    readonly readyCapacity: number;
    readonly queuedDecisions: number;
    readonly readyWorkers: number;
    readonly runningWorkers: number;
    readonly desiredWorkers: number;
};

function chartData(
    history: Array<SwarmWorkerFleetSample>,
): Array<SwarmWorkerHistoryPoint> {
    return history.map((sample) => ({
        label: formatInTimeZone(
            new Date(sample.observed_at),
            OUTREACH_ANALYTICS_TIMEZONE,
            'M/d h:mm a',
        ),
        processingDecisions: sample.processing_decisions,
        readyCapacity: sample.ready_capacity,
        queuedDecisions: sample.queued_decisions,
        readyWorkers: sample.ready_workers,
        runningWorkers: sample.running_workers,
        desiredWorkers: sample.desired_workers,
    }));
}

function EmptyHistory() {
    return (
        <div className="text-muted-foreground flex h-64 items-center justify-center rounded-md border text-center text-sm">
            History begins collecting when this release reaches the worker
            fleet.
        </div>
    );
}

export function SwarmWorkerHistoryChart({
    history,
}: SwarmWorkerHistoryChartProps) {
    const data = chartData(history);

    return (
        <div className="grid gap-4 xl:grid-cols-2">
            <Card>
                <CardHeader>
                    <CardTitle>Slot usage · trailing 24 hours</CardTitle>
                    <CardDescription>
                        Decisions processing now, ready process capacity, and
                        work still waiting for a slot.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {data.length === 0 ? (
                        <EmptyHistory />
                    ) : (
                        <div className="h-64 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={data}
                                    margin={{ left: 8, right: 8 }}
                                >
                                    <CartesianGrid
                                        stroke="var(--border)"
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="label"
                                        tick={{ fontSize: 11 }}
                                        minTickGap={32}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11 }}
                                        width={44}
                                        allowDecimals={false}
                                    />
                                    <Tooltip />
                                    <Line
                                        type="monotone"
                                        dataKey="processingDecisions"
                                        name="Slots in use"
                                        stroke="var(--chart-4)"
                                        dot={false}
                                        strokeWidth={2}
                                        isAnimationActive={false}
                                    />
                                    <Line
                                        type="stepAfter"
                                        dataKey="readyCapacity"
                                        name="Ready capacity"
                                        stroke="var(--chart-1)"
                                        dot={false}
                                        strokeWidth={2}
                                        isAnimationActive={false}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="queuedDecisions"
                                        name="Waiting"
                                        stroke="var(--chart-3)"
                                        dot={false}
                                        strokeDasharray="5 5"
                                        strokeWidth={2}
                                        isAnimationActive={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <CardTitle>Worker count · trailing 24 hours</CardTitle>
                    <CardDescription>
                        Ready processes compared with ECS running and desired
                        task counts.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {data.length === 0 ? (
                        <EmptyHistory />
                    ) : (
                        <div className="h-64 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={data}
                                    margin={{ left: 8, right: 8 }}
                                >
                                    <CartesianGrid
                                        stroke="var(--border)"
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="label"
                                        tick={{ fontSize: 11 }}
                                        minTickGap={32}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11 }}
                                        width={36}
                                        allowDecimals={false}
                                    />
                                    <Tooltip />
                                    <Line
                                        type="stepAfter"
                                        dataKey="readyWorkers"
                                        name="Ready workers"
                                        stroke="var(--chart-2)"
                                        dot={false}
                                        strokeWidth={2}
                                        isAnimationActive={false}
                                    />
                                    <Line
                                        type="stepAfter"
                                        dataKey="runningWorkers"
                                        name="ECS running"
                                        stroke="var(--chart-1)"
                                        dot={false}
                                        strokeWidth={2}
                                        isAnimationActive={false}
                                    />
                                    <Line
                                        type="stepAfter"
                                        dataKey="desiredWorkers"
                                        name="ECS desired"
                                        stroke="var(--chart-3)"
                                        dot={false}
                                        strokeDasharray="5 5"
                                        strokeWidth={2}
                                        isAnimationActive={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
